import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import React from "react";
import { toast } from "react-hot-toast";
import { Button } from "../../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/Card";
import { Input } from "../../../components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/Select";
import { fetchWithAuth } from "../../../hooks/useAuth";

type MetaType = "STRING" | "INTEGER" | "BOOLEAN" | "DATE" | "JSON";

interface ProductMetafield {
  id: number;
  productId: number;
  namespace: string;
  key: string;
  value: string;
  type: MetaType;
}

export default function ProductMetafieldsPannel({
  productId,
}: {
  productId: number;
}) {
  const queryClient = useQueryClient();

  const { data: metafields, isLoading } = useQuery<ProductMetafield[]>({
    queryKey: ["product-metafields", productId],
    queryFn: async () => {
      const res = await fetchWithAuth(
        `/api/admin/products/${productId}/metafields`
      );
      if (!res.ok) throw new Error("Eroare la încărcarea metafieldurilor");
      const json = await res.json();
      if (Array.isArray(json)) return json as ProductMetafield[];
      if (json && Array.isArray(json.date))
        return json.date as ProductMetafield[];
      if (json && Array.isArray(json.data))
        return json.data as ProductMetafield[];
      return [] as ProductMetafield[];
    },
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["product-metafields", productId],
    });
  };

  const createMeta = useMutation({
    mutationFn: async (payload: {
      namespace: string;
      key: string;
      value: string;
      type: MetaType;
    }) => {
      const res = await fetchWithAuth(
        `/api/admin/products/${productId}/metafields`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (res.status === 409)
          throw new Error(err.message || "Metafield deja existent");
        throw new Error(err.message || "Eroare la crearea metafieldului");
      }
      return res.json();
    },
    onSuccess: async () => {
      toast.success("Metafield creat");
      await invalidate();
      setNewMeta({ namespace: "", key: "", value: "", type: "STRING" });
    },
    onError: (e: any) => toast.error(e?.message || "Eroare necunoscută"),
  });

  const updateMeta = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<ProductMetafield>;
    }) => {
      const res = await fetchWithAuth(`/api/admin/products/metafields/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...(data.namespace !== undefined
            ? { namespace: data.namespace }
            : {}),
          ...(data.key !== undefined ? { key: data.key } : {}),
          ...(data.value !== undefined ? { value: data.value } : {}),
          ...(data.type !== undefined ? { type: data.type } : {}),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (res.status === 409)
          throw new Error(err.message || "Metafield deja existent");
        throw new Error(err.message || "Eroare la actualizare");
      }
      return res.json();
    },
    onSuccess: async () => {
      await invalidate();
      toast.success("Metafield actualizat");
    },
    onError: (e: any) => toast.error(e?.message || "Eroare necunoscută"),
  });

  const deleteMeta = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetchWithAuth(`/api/admin/products/metafields/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la ștergere");
      }
      return true;
    },
    onSuccess: async () => {
      toast.success("Metafield șters");
      await invalidate();
    },
    onError: (e: any) => toast.error(e?.message || "Eroare necunoscută"),
  });

  const [newMeta, setNewMeta] = React.useState<{
    namespace: string;
    key: string;
    value: string;
    type: MetaType;
  }>({ namespace: " ", key: "", value: "", type: "STRING" });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Metafields</span>
          <Button
            onClick={() => {
              if (!newMeta.key.trim() || !newMeta.value.trim()) {
                toast.error("Completează key și value");
                return;
              }
              createMeta.mutate({
                namespace: newMeta.namespace,
                key: newMeta.key.trim(),
                value: newMeta.value,
                type: newMeta.type,
              });
            }}
            disabled={createMeta.isPending}
            className="flex items-center gap-2"
          >
            {createMeta.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Adaugă metafield
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 ">
        {/* Create row */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 bg-background rounded-md p-3">
         
          <Input
            placeholder="key"
            value={newMeta.key}
            onChange={(e) => setNewMeta((s) => ({ ...s, key: e.target.value }))}
          />
          <Input
            placeholder="value"
            value={newMeta.value}
            onChange={(e) =>
              setNewMeta((s) => ({ ...s, value: e.target.value }))
            }
          />
          <Select
            value={newMeta.type}
            onValueChange={(v) =>
              setNewMeta((s) => ({ ...s, type: v as MetaType }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Tip" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="STRING">STRING</SelectItem>
              <SelectItem value="INTEGER">INTEGER</SelectItem>
              <SelectItem value="BOOLEAN">BOOLEAN</SelectItem>
              <SelectItem value="DATE">DATE</SelectItem>
              <SelectItem value="JSON">JSON</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center justify-end">
            <Button
              onClick={() => {
                if (!newMeta.key.trim() || !newMeta.value.trim()) {
                  toast.error("Completează key și value");
                  return;
                }
                createMeta.mutate({
                  namespace: newMeta.namespace,
                  key: newMeta.key.trim(),
                  value: newMeta.value,
                  type: newMeta.type,
                });
              }}
              disabled={createMeta.isPending}
            >
              {createMeta.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span className="ml-2">Salvează</span>
            </Button>
          </div>
        </div>

        {/* List */}
        {isLoading ? (
          <div className="text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Se încarcă
            metafielduri...
          </div>
        ) : !metafields || metafields.length === 0 ? (
          <div className="text-muted-foreground">Nu există metafielduri</div>
        ) : (
          <div className="space-y-2 flex flex-col items-start">
            {metafields.map((m) => (
              <div
                key={m.id}
                className="grid grid-cols-1 md:grid-cols-5 gap-3 p-3 "
              >
                <Input
                  defaultValue={m.key}
                  onBlur={(e) =>
                    updateMeta.mutate({
                      id: m.id,
                      data: { key: e.target.value },
                    })
                  }
                />
                <Input
                  defaultValue={m.value}
                  onBlur={(e) =>
                    updateMeta.mutate({
                      id: m.id,
                      data: { value: e.target.value },
                    })
                  }
                />
                <Select
                  defaultValue={m.type}
                  onValueChange={(v) =>
                    updateMeta.mutate({
                      id: m.id,
                      data: { type: v as MetaType },
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tip" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="STRING">STRING</SelectItem>
                    <SelectItem value="INTEGER">INTEGER</SelectItem>
                    <SelectItem value="BOOLEAN">BOOLEAN</SelectItem>
                    <SelectItem value="DATE">DATE</SelectItem>
                    <SelectItem value="JSON">JSON</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Button
                    variant="destructive"
                    onClick={() => deleteMeta.mutate(m.id)}
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" /> Șterge
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
